import { useEffect, useState } from 'react';
import { useLeagueStore } from '../../lib/leagueStore';
import { useThemeStore } from '../../lib/themeStore';
import { supabase } from '../../lib/supabase';
import { translate } from '../../lib/translations';
import { Calendar } from 'lucide-react';

interface FreeAgentSigning {
  fa_signing_id: number;
  league_id: number;
  player_id: number;
  user_id: number;
  created_at: string;
  fromfreeagent: boolean;
  salary?: number | null;
  year_left?: number | null;
}

interface Player {
  playerid: number;
  skaterfullname?: string | null;
  gamesplayed?: number | null;
  goals?: number | null;
  assists?: number | null;
  points?: number | null;
  salary?: number | null;
}

interface Goalie {
  playerid: number;
  goaliefullname?: string | null;
  gamesplayed?: number | null;
  wins?: number | null;
  savepct?: number | null;
  shutouts?: number | null;
  salary?: number | null;
}

interface UserShort {
  user_id: number;
  username?: string | null;
}

interface Props {
  language: string;
}

export function FreeAgentSignings({ language }: Props) {
  const selectedLeague = useLeagueStore((s) => s.selectedLeague);
  const { theme } = useThemeStore();

  const [items, setItems] = useState<FreeAgentSigning[]>([]);
  const [playersMap, setPlayersMap] = useState<Record<number, Player>>({});
  const [goaliesMap, setGoaliesMap] = useState<Record<number, Goalie>>({});
  const [usersMap, setUsersMap] = useState<Record<number, UserShort>>({});
  const [loading, setLoading] = useState(true);

  const formatSalary = (value?: number | null) => {
    if (value == null) return '';
    // If salary >= 1,000,000 -> show in M$ with comma as decimal separator (ex: 5,5 M$)
    if (Math.abs(value) >= 1000000) {
      const millions = value / 1000000;
      // use comma as decimal separator with one decimal when needed
      const formatted = Math.round(millions * 10) / 10;
      return `${formatted.toString().replace('.', ',')} M$`;
    }

    // If salary >= 1000 -> show in k$
    if (Math.abs(value) >= 1000) {
      const thousands = Math.round(value / 1000);
      return `${thousands} k$`;
    }

    return `${value} $`;
  };

  useEffect(() => {
    if (selectedLeague?.id) loadSignings();
  }, [selectedLeague]);

  const loadSignings = async () => {
    if (!selectedLeague) return;
    setLoading(true);

    try {
      const { data, error } = await supabase
        .from('freeagentsigninghistory')
        .select('*')
        .eq('league_id', selectedLeague.id)
        .eq('fromfreeagent', true)
        .order('created_at', { ascending: false });

      if (error) throw error;

      const rows: FreeAgentSigning[] = (data || []) as FreeAgentSigning[];
      setItems(rows);

      // Batch fetch players, goalies, and users
      const playerIds = Array.from(new Set(rows.map(r => r.player_id).filter(Boolean)));
      const userIds = Array.from(new Set(rows.map(r => r.user_id).filter(Boolean)));

      if (playerIds.length > 0) {
        // Fetch skaters (players) - Utiliser la vue avec les stats du jour
        const { data: players } = await supabase
          .from('show_player_onlycurrentday')
          .select('playerid, skaterfullname, gamesplayed, goals, assists, points, salary')
          .in('playerid', playerIds as any[]);

        const playersMapTemp: Record<number, Player> = {};
        (players || []).forEach((p: any) => playersMapTemp[p.playerid] = p);
        setPlayersMap(playersMapTemp);

        // Fetch goalies - Utiliser la vue avec les stats du jour
        const { data: goalies } = await supabase
          .from('show_goalie_onlycurrentday')
          .select('playerid, goaliefullname, gamesplayed, wins, savepct, shutouts, salary')
          .in('playerid', playerIds as any[]);

        const goaliesMapTemp: Record<number, Goalie> = {};
        (goalies || []).forEach((g: any) => goaliesMapTemp[g.playerid] = g);
        setGoaliesMap(goaliesMapTemp);
      }

      if (userIds.length > 0) {
        const { data: users } = await supabase
          .from('users')
          .select('user_id, username')
          .in('user_id', userIds as any[]);

        const map: Record<number, UserShort> = {};
        (users || []).forEach((u: any) => map[u.user_id] = u);
        setUsersMap(map);
      }

    } catch (err) {
      console.error('Error loading free agent signings', err);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="text-center p-5">
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">{translate('loading', language as 'fr' | 'en')}</span>
        </div>
        <p className="mt-3 text-muted">Chargement des signatures d'agents libres...</p>
      </div>
    );
  }

  if (items.length === 0) {
    return (
      <div className="text-center p-5">
        <Calendar size={48} className="text-muted mb-3" />
        <h3 className="text-muted">Aucune signature d'agent libre</h3>
        <p className="text-muted">Il n'y a pas encore de signatures d'agents libres dans cette ligue.</p>
      </div>
    );
  }

  return (
    <div>
      <div className="row mb-4">
        <div className="col-12">
          <div className="card border-0 shadow-sm" style={{
            background: theme === 'dark' 
              ? 'linear-gradient(135deg, rgba(30, 41, 59, 0.8) 0%, rgba(15, 23, 42, 0.8) 100%)'
              : 'linear-gradient(135deg, rgba(248, 250, 252, 0.8) 0%, rgba(241, 245, 249, 0.8) 100%)',
            backdropFilter: 'blur(10px)'
          }}>
            <div className="card-body p-4">
              <div className="d-flex align-items-center">
                <Calendar size={24} className="text-primary me-3" />
                <div>
                  <h5 className={`mb-1 ${theme === 'dark' ? 'text-white' : 'text-dark'}`}>Signatures d'agents libres</h5>
                  <p className="text-muted mb-0">{items.length} signature{items.length > 1 ? 's' : ''}</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="list-group">
        {items.map(item => {
          const player = playersMap[item.player_id];
          const goalie = goaliesMap[item.player_id];
          const user = usersMap[item.user_id];
          const key = item.fa_signing_id !== undefined && item.fa_signing_id !== null
            ? `fas-${item.fa_signing_id}`
            : `fas-${item.player_id}-${item.user_id}-${encodeURIComponent(item.created_at || '')}`;

          // Determine if this is a goalie or a player
          const isGoalie = goalie && !player;
          const playerName = isGoalie 
            ? goalie.goaliefullname 
            : player?.skaterfullname;

          return (
            <div key={key} className="list-group-item">
              <div className="d-flex flex-column flex-md-row justify-content-between align-items-start">
                <div className="mb-2 mb-md-0">
                  <div className={`fw-semibold ${theme === 'dark' ? 'text-white' : 'text-dark'}`}>
                    {playerName || `#${item.player_id}`}
                  </div>
                  <div className="text-muted small">DG: {user?.username || `#${item.user_id}`}</div>
                </div>

                <div className="d-flex align-items-center gap-3 mb-2 mb-md-0">
                  {isGoalie ? (
                    // Display goalie stats
                    <>
                      <div className="d-flex flex-column text-center me-2">
                        <small className="text-muted">GP</small>
                        <div className={`fw-semibold ${theme === 'dark' ? 'text-white' : 'text-dark'}`}>{goalie?.gamesplayed ?? '-'}</div>
                      </div>
                      <div className="d-flex flex-column text-center me-2">
                        <small className="text-muted">W</small>
                        <div className={`fw-semibold ${theme === 'dark' ? 'text-white' : 'text-dark'}`}>{goalie?.wins ?? '-'}</div>
                      </div>
                      <div className="d-flex flex-column text-center me-2">
                        <small className="text-muted">SV%</small>
                        <div className={`fw-semibold ${theme === 'dark' ? 'text-white' : 'text-dark'}`}>
                          {goalie?.savepct != null ? (goalie.savepct * 100).toFixed(1) : '-'}
                        </div>
                      </div>
                      <div className="d-flex flex-column text-center">
                        <small className="text-muted">SO</small>
                        <div className={`fw-semibold ${theme === 'dark' ? 'text-white' : 'text-dark'}`}>{goalie?.shutouts ?? '-'}</div>
                      </div>
                    </>
                  ) : (
                    // Display player stats
                    <>
                      <div className="d-flex flex-column text-center me-2">
                        <small className="text-muted">GP</small>
                        <div className={`fw-semibold ${theme === 'dark' ? 'text-white' : 'text-dark'}`}>{player?.gamesplayed ?? '-'}</div>
                      </div>
                      <div className="d-flex flex-column text-center me-2">
                        <small className="text-muted">G</small>
                        <div className={`fw-semibold ${theme === 'dark' ? 'text-white' : 'text-dark'}`}>{player?.goals ?? '-'}</div>
                      </div>
                      <div className="d-flex flex-column text-center me-2">
                        <small className="text-muted">A</small>
                        <div className={`fw-semibold ${theme === 'dark' ? 'text-white' : 'text-dark'}`}>{player?.assists ?? '-'}</div>
                      </div>
                      <div className="d-flex flex-column text-center">
                        <small className="text-muted">P</small>
                        <div className={`fw-semibold ${theme === 'dark' ? 'text-white' : 'text-dark'}`}>{player?.points ?? '-'}</div>
                      </div>
                    </>
                  )}
                </div>

                <div className="text-end small text-muted" style={{ minWidth: 120 }}>
                  <div>{new Date(item.created_at).toLocaleString('fr-FR')}</div>
                  <div>
                    {(item.salary ?? (isGoalie ? goalie?.salary : player?.salary)) != null 
                      ? `Salaire: ${formatSalary(item.salary ?? (isGoalie ? goalie?.salary : player?.salary))}` 
                      : ''}
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

export default FreeAgentSignings;
